import { z } from 'zod';
import { ohneSteuerzeichen } from './steuerzeichen';
import { TELEFON_E164_RE } from './lead';
import { HONEYPOT_FIELD_NAME, honeypotFieldSchema } from './honeypot';
import { attributionSchema } from './attribution';
/**
 * Public Vor-Ort-Bewertung intake (POST /ingest/vor-ort) — the «Immobilie
 * verkaufen» form on mlg-ag.ch. The contract is `docs/VOR-ORT-FORMULIER-CONTRACT.md`
 * (§2 the fields, §3 the transport): the edge worker validates with this exact
 * schema and forwards the PARSED body HMAC-signed; the backend re-validates.
 *
 * Unknown keys are DROPPED, not rejected (no `.strict()`, like
 * `beratungIngestSchema`): an older page must never lose a request because the
 * server learned a new field first. A new field therefore ships backend and
 * edge first, the page second.
 */
/** «Welche Immobilie möchten Sie verkaufen?» — the first five are the words of the mandate checklist (`IMMOBILIEN_TYPES`). */
export const VOR_ORT_OBJEKTARTEN = [
    'efh',
    'wohnung',
    'mfh',
    'grundstueck',
    'gewerbe',
    'sonstiges',
];
/** «Wann passt Ihnen ein Besuch?» — the wish for the VISIT, never a call-back deadline (owner 25-9, contract §8 question 2). */
export const VOR_ORT_ZEITRAEUME = [
    'so_schnell_wie_moeglich',
    'diese_woche',
    'naechste_woche',
    'flexibel',
];
/** The preferred call-back time — the same four words as the Beratung widget's `PREFERRED_TIMES`. */
export const VOR_ORT_ERREICHBARKEITEN = ['vormittags', 'mittags', 'nachmittags', 'abends'];
/**
 * The display words (contract §2.1) — ONE list for the CRM, the task title and
 * the internal notice, so a renamed value cannot drift between them.
 */
export const VOR_ORT_OBJEKTART_LABELS = {
    efh: 'Einfamilienhaus',
    wohnung: 'Wohnung',
    mfh: 'Mehrfamilienhaus',
    grundstueck: 'Grundstück',
    gewerbe: 'Gewerbe',
    sonstiges: 'Sonstiges',
};
export const VOR_ORT_ZEITRAUM_LABELS = {
    so_schnell_wie_moeglich: 'So schnell wie möglich',
    diese_woche: 'Diese Woche',
    naechste_woche: 'Nächste Woche',
    flexibel: 'Flexibel',
};
export const VOR_ORT_ERREICHBARKEIT_LABELS = {
    vormittags: 'Vormittags',
    mittags: 'Mittags',
    nachmittags: 'Nachmittags',
    abends: 'Abends',
};
/** A Swiss postcode — the same rule as the valuation widget, checked on the server too. */
export const VOR_ORT_PLZ_RE = /^[1-9][0-9]{3}$/;
/** The HOST page's path (no query, no fragment) — empty until ANALYTICS phase B (contract §2). */
export const VOR_ORT_LANDING_PAGE_RE = /^\/[^?#]*$/;
/**
 * De stuurteken-wal van `steuerzeichen.ts` (geen LF) voor elk vrij tekstveld dat
 * de database of de router bereikt: de namen (micro-B B1) en `consentVersion` en
 * `landingPage` (micro-B R1, review #683 NIT-1 — een NUL daar werd anders een
 * Postgres-fout 22021, dus een 500 in plaats van een 400).
 */
const vorOrtZonderSteuerzeichen = (v) => ohneSteuerzeichen(v);
export const vorOrtIngestSchema = z.object({
    objektart: z.enum(VOR_ORT_OBJEKTARTEN),
    plz: z.string().trim().regex(VOR_ORT_PLZ_RE),
    zeitraum: z.enum(VOR_ORT_ZEITRAEUME),
    erreichbarkeit: z.enum(VOR_ORT_ERREICHBARKEITEN).optional(),
    /**
     * Micro-B B1: de stuurteken-wal van `steuerzeichen.ts` VÓÓR de trim — geen LF,
     * geen CR/tab/NUL (de naam gaat eenregelig naar de router, `to.name`).
     */
    vorname: z.string().refine(vorOrtZonderSteuerzeichen, 'steuerzeichen').trim().min(1).max(100),
    nachname: z.string().refine(vorOrtZonderSteuerzeichen, 'steuerzeichen').trim().min(1).max(100),
    /** E.164 for CH, DE or AT — the widget normalises before it sends (`apps/immo-map/src/telefon.ts`). */
    telefon: z.string().trim().regex(TELEFON_E164_RE),
    email: z.string().trim().toLowerCase().email().max(254),
    /** The privacy consent — the form cannot be sent without it. */
    consent: z.literal(true),
    /** The id of the privacy text the visitor saw (e.g. `vor-ort-privacy-2026.1`), kept in the consent record. */
    consentVersion: z
        .string()
        .refine(vorOrtZonderSteuerzeichen, 'steuerzeichen')
        .trim()
        .min(1)
        .max(64),
    /** Campaign attribution — click ids only without a declined ad consent (contract §7); stored under the owner brake. */
    utm: attributionSchema,
    landingPage: z
        .string()
        .max(512)
        .regex(VOR_ORT_LANDING_PAGE_RE)
        .refine(vorOrtZonderSteuerzeichen, 'steuerzeichen')
        .optional(),
    /** Cloudflare Turnstile token, action `vor_ort` (verified by the backend live, by the edge in mock mode). */
    turnstileToken: z.string().min(1).max(4096),
    /** Honeypot — same contract, same key, as the other public forms. */
    [HONEYPOT_FIELD_NAME]: honeypotFieldSchema,
});
/**
 * V2 — the answer of `GET /leads/:id/vor-ort`, read by the CRM card
 * «Vor-Ort-Termin». `anfrage` is the ONE Vor-Ort-Anfrage behind the lead
 * (`vor_ort_anfragen.leadId` is unique: every request mints its own lead), or
 * `null` for every lead that did not come from the form — no card then.
 *
 * The values are the technical words; the CRM translates them with the label
 * maps above and shows an unknown word as it is, so a value the backend learns
 * first never breaks the card (hence `z.string()`, as in the B7 answer).
 * `taskId` = the Rückruf task the request created (null for a test lead).
 */
export const leadVorOrtAnfrageSchema = z
    .object({
    id: z.string(),
    objektart: z.string(),
    plz: z.string(),
    zeitraum: z.string(),
    erreichbarkeit: z.string().nullable(),
    landingPage: z.string().nullable(),
    taskId: z.string().nullable(),
    createdAt: z.string().datetime(),
})
    .strict();
export const leadVorOrtAntwortSchema = z
    .object({ anfrage: leadVorOrtAnfrageSchema.nullable() })
    .strict();
